import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Pencil } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Can } from '@/components/Can';
import { ImportExportMenu } from '@/components/ImportExportMenu';
import { RefreshButton } from '@/components/RefreshButton';
import { Badge, Button, Field, Input, Select, Textarea, useConfirm, useToast, } from '@/components/ui';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
const TEMPLATES = [
    { key: 'ifrs', label: 'IFRS (general)' },
    { key: 'uk_frs_102', label: 'UK FRS 102' },
    { key: 'pk_secp', label: 'Pakistan SECP' },
    { key: 'ksa_socpa', label: 'KSA SOCPA' },
    { key: 'uae_fta', label: 'UAE FTA' },
];
const TYPE_COLORS = {
    asset: 'text-blue-700',
    liability: 'text-amber-700',
    equity: 'text-purple-700',
    income: 'text-green-700',
    expense: 'text-rose-700',
};
const SUBTYPE_OPTIONS = [
    ['current_asset', 'Current Asset'],
    ['cash_bank', 'Cash & Bank'],
    ['accounts_receivable', 'Accounts Receivable'],
    ['inventory_asset', 'Inventory'],
    ['fixed_asset', 'Fixed Asset'],
    ['current_liability', 'Current Liability'],
    ['accounts_payable', 'Accounts Payable'],
    ['tax_payable', 'Tax Payable'],
    ['long_term_liability', 'Long-Term Liability'],
    ['owners_equity', "Owners' Equity"],
    ['retained_earnings', 'Retained Earnings'],
    ['operating_income', 'Operating Income'],
    ['other_income', 'Other Income'],
    ['operating_expense', 'Operating Expense'],
    ['cogs', 'Cost of Goods Sold'],
    ['tax_expense', 'Tax Expense'],
];
export function ChartOfAccountsPage() {
    const qc = useQueryClient();
    const toast = useToast();
    // Persist filters in the URL so they survive navigation.
    const [params, setParams] = useSearchParams();
    const typeFilter = params.get('type') ?? '';
    const search = params.get('q') ?? '';
    const setTypeFilter = (v) => { v ? params.set('type', v) : params.delete('type'); setParams(params, { replace: true }); };
    const setSearch = (v) => { v ? params.set('q', v) : params.delete('q'); setParams(params, { replace: true }); };
    const tree = useQuery({
        queryKey: ['accounts-tree'],
        queryFn: async () => (await api.get('/accounting/accounts/tree/')).data,
    });
    const [createOpen, setCreateOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const applyTemplate = useMutation({
        mutationFn: async (template) => api.post('/accounting/accounts/apply-template/', { template_name: template }),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['accounts-tree'] });
            toast.success('Template applied');
        },
        onError: (err) => toast.error('Failed', err.response?.data?.detail ?? err.message),
    });
    /** Count every node in the tree, not just the L1 roots — so the
     *  header shows "131 accounts" instead of "7 categories". */
    const totalCount = useMemo(() => {
        let n = 0;
        const walk = (nodes) => {
            for (const node of nodes) {
                n++;
                if (node.children?.length)
                    walk(node.children);
            }
        };
        walk(tree.data ?? []);
        return n;
    }, [tree.data]);
    /** Filter the tree: a node is kept if it matches OR any descendant
     *  matches. We always keep the matched node's ancestors so the
     *  hierarchy stays visible (matches Oracle Fusion / QuickBooks COA UX). */
    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        const matches = (n) => (!typeFilter || n.type === typeFilter)
            && (!q || n.code.toLowerCase().includes(q) || n.name.toLowerCase().includes(q));
        function prune(nodes) {
            const out = [];
            for (const n of nodes) {
                const keptChildren = n.children?.length ? prune(n.children) : [];
                if (matches(n) || keptChildren.length > 0) {
                    out.push({ ...n, children: keptChildren });
                }
            }
            return out;
        }
        return prune(tree.data ?? []);
    }, [tree.data, typeFilter, search]);
    /** Which parent nodes are expanded. Default: every L1 + every L2 open;
     *  L3 collapsed by default so the page isn't a wall of text. */
    const [expanded, setExpanded] = useState(new Set());
    useEffect(() => {
        if (!tree.data)
            return;
        const next = new Set();
        const collect = (nodes, depth) => {
            for (const n of nodes) {
                if (depth <= 1 && n.children?.length)
                    next.add(n.id);
                if (n.children?.length)
                    collect(n.children, depth + 1);
            }
        };
        collect(tree.data, 0);
        setExpanded(next);
    }, [tree.data]);
    // When the user is searching, auto-expand everything so matches surface.
    const isSearching = Boolean(typeFilter || search);
    const allExpandedIds = useMemo(() => {
        if (!isSearching)
            return null;
        const ids = new Set();
        const walk = (nodes) => {
            for (const n of nodes) {
                if (n.children?.length) {
                    ids.add(n.id);
                    walk(n.children);
                }
            }
        };
        walk(filtered);
        return ids;
    }, [isSearching, filtered]);
    const effectiveExpanded = allExpandedIds ?? expanded;
    function toggle(id) {
        setExpanded((cur) => {
            const next = new Set(cur);
            if (next.has(id))
                next.delete(id);
            else
                next.add(id);
            return next;
        });
    }
    function expandAll() {
        const ids = new Set();
        const walk = (nodes) => {
            for (const n of nodes) {
                if (n.children?.length) {
                    ids.add(n.id);
                    walk(n.children);
                }
            }
        };
        walk(tree.data ?? []);
        setExpanded(ids);
    }
    function collapseAll() { setExpanded(new Set()); }
    return (_jsxs("div", { className: "space-y-4", children: [_jsxs("header", { className: "flex items-center justify-between", children: [_jsxs("div", { children: [_jsx("h1", { className: "text-2xl font-semibold", children: "Chart of Accounts" }), _jsxs("p", { className: "text-sm text-muted-foreground", children: [totalCount, " accounts across L1 \u2192 L4."] })] }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx(RefreshButton, { queryKeys: [['accounts-tree']], label: "Fetch" }), _jsx(ImportExportMenu, { resource: "/accounting/accounts", onImported: () => qc.invalidateQueries({ queryKey: ['account-tree'] }) }), _jsxs(Can, { code: "accounting.account.create", children: [_jsxs("select", { defaultValue: "", onChange: (e) => {
                                            if (e.target.value) {
                                                applyTemplate.mutate(e.target.value);
                                                e.target.value = '';
                                            }
                                        }, className: "rounded-md border border-input bg-background px-3 py-2 text-sm", children: [_jsx("option", { value: "", children: "Apply template\u2026" }), TEMPLATES.map((t) => _jsx("option", { value: t.key, children: t.label }, t.key))] }), _jsx(Button, { onClick: () => setCreateOpen(true), children: "+ Account" })] })] })] }), _jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [_jsx("input", { placeholder: "Search code or name\u2026", value: search, onChange: (e) => setSearch(e.target.value), className: "block w-64 rounded-md border border-input bg-background px-3 py-2 text-sm" }), _jsxs("select", { value: typeFilter, onChange: (e) => setTypeFilter(e.target.value), className: "rounded-md border border-input bg-background px-3 py-2 text-sm", children: [_jsx("option", { value: "", children: "All types" }), _jsx("option", { value: "asset", children: "Asset" }), _jsx("option", { value: "liability", children: "Liability" }), _jsx("option", { value: "equity", children: "Equity" }), _jsx("option", { value: "income", children: "Income" }), _jsx("option", { value: "expense", children: "Expense" })] }), (typeFilter || search) && (_jsx("button", { onClick: () => { setTypeFilter(''); setSearch(''); }, className: "text-xs text-primary hover:underline", children: "Clear filters" })), _jsxs("div", { className: "ms-auto flex gap-2 text-xs", children: [_jsx("button", { onClick: expandAll, className: "rounded-md border border-input px-2 py-1 hover:bg-muted", children: "Expand all" }), _jsx("button", { onClick: collapseAll, className: "rounded-md border border-input px-2 py-1 hover:bg-muted", children: "Collapse all" })] })] }), _jsxs("ul", { className: "divide-y rounded-md border bg-card", children: [filtered.map((node) => (_jsx(TreeRow, { node: node, depth: 0, expanded: effectiveExpanded, onToggle: toggle, onEdit: setEditId }, node.id))), filtered.length === 0 && (_jsx("li", { className: "px-4 py-8 text-center text-sm text-muted-foreground", children: "No accounts. Apply a template to get started." }))] }), createOpen && (_jsx(AccountModal, { mode: "create", onClose: () => setCreateOpen(false), onSaved: () => qc.invalidateQueries({ queryKey: ['accounts-tree'] }) })), editId && (_jsx(AccountModal, { mode: "edit", id: editId, onClose: () => setEditId(null), onSaved: () => qc.invalidateQueries({ queryKey: ['accounts-tree'] }) }))] }));
}
function AccountModal({ mode, id, onClose, onSaved }) {
    const toast = useToast();
    const { confirm } = useConfirm();
    const existing = useQuery({
        queryKey: ['account', id],
        queryFn: async () => (await api.get(`/accounting/accounts/${id}/`)).data,
        enabled: mode === 'edit' && Boolean(id),
    });
    const [form, setForm] = useState({
        code: '', name: '', subtype: 'current_asset',
        description: '', reconciliation_required: false, is_archived: false,
    });
    const [loaded, setLoaded] = useState(mode === 'create');
    // Hydrate form when the API returns the saved record — this is the part that
    // was broken before: edits showed defaults instead of saved values.
    useEffect(() => {
        if (existing.data) {
            setForm({
                code: existing.data.code,
                name: existing.data.name,
                subtype: existing.data.subtype,
                description: existing.data.description ?? '',
                reconciliation_required: existing.data.reconciliation_required,
                is_archived: existing.data.is_archived,
            });
            setLoaded(true);
        }
    }, [existing.data]);
    const save = useMutation({
        mutationFn: async () => {
            if (mode === 'create') {
                return (await api.post('/accounting/accounts/', form)).data;
            }
            return (await api.patch(`/accounting/accounts/${id}/`, form)).data;
        },
        onSuccess: () => {
            toast.success(mode === 'create' ? 'Account created' : 'Account saved');
            onSaved();
            onClose();
        },
        onError: (err) => toast.error('Save failed', JSON.stringify(err.response?.data ?? err.message)),
    });
    const isSystem = existing.data?.is_system;
    const disabled = mode === 'edit' && !loaded;
    return (_jsx("div", { className: "fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4", onClick: onClose, children: _jsxs("form", { className: "w-full max-w-lg space-y-4 rounded-lg border bg-card p-6 shadow-lg", onClick: (e) => e.stopPropagation(), onSubmit: (e) => { e.preventDefault(); save.mutate(); }, children: [_jsxs("div", { className: "flex items-center justify-between", children: [_jsx("h2", { className: "text-lg font-semibold", children: mode === 'create' ? 'New account' : `Edit · ${form.code}` }), isSystem && _jsx(Badge, { tone: "warning", children: "system" })] }), disabled ? (_jsx("p", { className: "text-sm text-muted-foreground", children: "Loading saved values\u2026" })) : (_jsxs(_Fragment, { children: [_jsxs("div", { className: "grid grid-cols-2 gap-3", children: [_jsx(Field, { label: "Code", children: _jsx(Input, { required: true, value: form.code ?? '', disabled: isSystem, onChange: (e) => setForm({ ...form, code: e.target.value }) }) }), _jsx(Field, { label: "Subtype", children: _jsx(Select, { value: form.subtype ?? '', disabled: isSystem, onChange: (e) => setForm({ ...form, subtype: e.target.value }), children: SUBTYPE_OPTIONS.map(([v, l]) => _jsx("option", { value: v, children: l }, v)) }) })] }), _jsx(Field, { label: "Name", children: _jsx(Input, { required: true, value: form.name ?? '', onChange: (e) => setForm({ ...form, name: e.target.value }) }) }), _jsx(Field, { label: "Description", children: _jsx(Textarea, { rows: 2, value: form.description ?? '', onChange: (e) => setForm({ ...form, description: e.target.value }) }) }), _jsxs("div", { className: "grid grid-cols-2 gap-3", children: [_jsxs("label", { className: "flex items-center gap-2 text-sm", children: [_jsx("input", { type: "checkbox", checked: !!form.reconciliation_required, onChange: (e) => setForm({ ...form, reconciliation_required: e.target.checked }) }), "Requires reconciliation"] }), mode === 'edit' && (_jsxs("label", { className: "flex items-center gap-2 text-sm", children: [_jsx("input", { type: "checkbox", checked: !!form.is_archived, disabled: isSystem, onChange: (e) => setForm({ ...form, is_archived: e.target.checked }) }), "Archived"] }))] })] })), _jsxs("div", { className: "flex items-center justify-between", children: [mode === 'edit' && !isSystem ? (_jsx(Can, { code: "accounting.account.update", children: _jsx("button", { type: "button", onClick: async () => {
                                    const ok = await confirm({
                                        title: `Delete account ${form.code}?`,
                                        description: 'This cannot be undone if no transactions reference it.',
                                        confirmLabel: 'Delete', tone: 'danger',
                                    });
                                    if (!ok)
                                        return;
                                    try {
                                        await api.delete(`/accounting/accounts/${id}/`);
                                        toast.success('Account deleted');
                                        onSaved();
                                        onClose();
                                    }
                                    catch (err) {
                                        toast.error('Delete failed', err.response?.data?.detail || err.message);
                                    }
                                }, className: "text-xs text-red-600 hover:underline", children: "Delete" }) })) : _jsx("span", {}), _jsxs("div", { className: "flex gap-2", children: [_jsx(Button, { variant: "outline", onClick: onClose, children: "Cancel" }), _jsx(Button, { type: "submit", loading: save.isPending, disabled: disabled, children: mode === 'create' ? 'Create' : 'Save changes' })] })] })] }) }));
}
/** Recursive tree row. Renders one account + its children (if expanded).
 *  Indentation, chevrons, type tags — what Oracle Fusion / QuickBooks /
 *  Xero all do. */
function TreeRow({ node, depth, expanded, onToggle, onEdit, }) {
    const hasChildren = (node.children?.length ?? 0) > 0;
    const isOpen = expanded.has(node.id);
    // Posting status: a leaf without a sub-ledger control flag is freely
    // postable; a sub-ledger control account is *visible* as a leaf but
    // *blocked* for direct GL postings — only AR/AP/FA/Inventory subledgers
    // may move it. Match the same logic the backend enforces.
    const isControl = node.recon_type && node.recon_type !== 'none';
    const isPostable = !hasChildren && !isControl;
    const reconBadge = node.recon_type === 'customer' ? 'AR ctl'
        : node.recon_type === 'vendor' ? 'AP ctl'
            : node.recon_type === 'fixed_asset' ? 'FA ctl'
                : node.recon_type === 'inventory' ? 'Inv ctl'
                    : node.recon_type === 'bank' ? 'Bank recon'
                        : null;
    return (_jsxs(_Fragment, { children: [_jsxs("li", { className: cn('group flex items-center justify-between gap-3 px-2 py-2 hover:bg-muted/30', node.is_archived && 'opacity-50', depth === 0 && 'bg-muted/20 font-semibold'), style: { paddingInlineStart: `${depth * 1.5 + 0.5}rem` }, children: [_jsxs("div", { className: "flex min-w-0 items-center gap-2 text-sm", children: [hasChildren ? (_jsx("button", { type: "button", onClick: () => onToggle(node.id), className: "shrink-0 rounded p-0.5 hover:bg-muted", "aria-label": isOpen ? 'Collapse' : 'Expand', children: isOpen
                                    ? _jsx(ChevronDown, { className: "h-3.5 w-3.5" })
                                    : _jsx(ChevronRight, { className: "h-3.5 w-3.5" }) })) : (_jsx("span", { className: "inline-block w-[1.125rem] shrink-0" })), _jsx("span", { className: "shrink-0 font-mono text-muted-foreground", children: node.code }), _jsx("span", { className: cn('truncate', depth === 0 ? 'font-bold' : 'font-medium'), children: node.name }), isPostable && (_jsx("span", { className: "rounded bg-green-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-green-800 dark:bg-green-900/40 dark:text-green-200", title: "Leaf account \u2014 can take direct journal postings.", children: "Postable" })), isControl && reconBadge && (_jsx("span", { className: "rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-800 dark:bg-amber-900/40 dark:text-amber-200", title: node.posting_blocked_reason || 'Sub-ledger control — only the sub-ledger may move this account.', children: reconBadge })), _jsx("span", { className: cn('hidden text-[11px] uppercase tracking-wide md:inline', TYPE_COLORS[node.type]), children: node.type }), _jsx(Badge, { tone: "neutral", children: node.subtype.replace(/_/g, ' ') }), node.is_system && (_jsx("span", { className: "hidden rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase md:inline", children: "system" }))] }), _jsx(Can, { code: "accounting.account.update", children: _jsxs("button", { type: "button", onClick: () => onEdit(node.id), className: "inline-flex shrink-0 items-center gap-1 rounded-md p-1.5 text-xs text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground group-hover:opacity-100", "aria-label": "Edit account", children: [_jsx(Pencil, { className: "h-3.5 w-3.5" }), " Edit"] }) })] }), hasChildren && isOpen && node.children.map((child) => (_jsx(TreeRow, { node: child, depth: depth + 1, expanded: expanded, onToggle: onToggle, onEdit: onEdit }, child.id)))] }));
}
